import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Megaphone, Send, Users, RefreshCw, Clock, XCircle, MessageSquareReply, ChevronDown, ChevronUp, Repeat, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import CampaignCompare from "@/components/CampaignCompare";
import RecurrenceCard from "@/components/RecurrenceCard";

const SEL = "w-full h-10 rounded-md border border-border bg-background px-3 text-sm";
const REDEEM_OPTS = [["", "Tutti"], ["pending", "Solo chi non ha ancora ritirato"], ["redeemed", "Solo chi ha già ritirato"]];
const STATUS = {
  scheduled: ["Programmata", "bg-amber-500/15 text-amber-400 border-amber-500/30"],
  running: ["In corso", "bg-sky-500/15 text-sky-400 border-sky-500/30"],
  completed: ["Completata", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"],
  cancelled: ["Annullata", "bg-red-500/15 text-red-400 border-red-500/30"],
};
const fmtIt = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const SUBMIT_ICON = { schedule: Clock, recurring: Repeat };
function SubmitIcon({ mode }) {
  const I = SUBMIT_ICON[mode] || Send;
  return <I className="h-4 w-4" />;
}
function submitLabel(mode, busy, rec) {
  if (busy) return "Invio…";
  if (mode === "schedule") return "Programma campagna";
  if (mode === "recurring") return "Attiva campagna ricorrente";
  return `Invia campagna${rec?.messages ? ` a ${rec.messages}` : ""}`;
}
function progressPct(c) {
  if (c.status === "scheduled") return 0;
  if (!c.total) return 100;
  return Math.round(((c.sent + c.failed) / c.total) * 100);
}

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [contests, setContests] = useState([]);
  const [stores, setStores] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [form, setForm] = useState({ contest_id: "", template_id: "", preview_text: "", name: "", store_id: "", prize_id: "", redeemed: "", mode: "now", scheduled_at: "", frequency: "weekly", send_time: "10:00", end_date: "", discount_on: false, discount_description: "", discount_days: 30, discount_prefix: "SC" });
  const [rec, setRec] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [recurrences, setRecurrences] = useState([]);
  const [busy, setBusy] = useState(false);
  const [myPhone, setMyPhone] = useState(() => localStorage.getItem("vw_my_phone") || "");
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(() => Promise.all([api.listCampaigns().then(setCampaigns), api.listRecurrences().then(setRecurrences)]).catch(() => {}), []);
  useEffect(() => {
    api.listContests().then(setContests).catch(() => {});
    api.listStores().then(setStores).catch(() => {});
    load();
  }, [load]);
  useEffect(() => {
    setPrizes([]);
    setForm((f) => ({ ...f, prize_id: "" }));
    if (form.contest_id) api.listPrizes(form.contest_id).then(setPrizes).catch(() => {});
  }, [form.contest_id]);
  const params = { contest_id: form.contest_id || undefined, store_id: form.store_id || undefined, prize_id: form.prize_id || undefined, redeemed: form.redeemed || undefined };
  useEffect(() => {
    api.campaignRecipients(params).then(setRec).catch(() => setRec(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.contest_id, form.store_id, form.prize_id, form.redeemed]);
  useEffect(() => {
    if (!campaigns.some((c) => c.status === "running")) return;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [campaigns, load]);

  const filters = { store_id: form.store_id, prize_id: form.prize_id, redeemed: form.redeemed };
  const discount = form.discount_on && form.discount_description.trim() ? { description: form.discount_description, valid_days: Number(form.discount_days) || 30, prefix: form.discount_prefix } : null;
  const sendPreview = async () => {
    const phone = myPhone.trim();
    if (!/^\+\d{8,15}$/.test(phone)) return toast.error("Inserisci il tuo numero in formato internazionale (+39...)");
    if (!form.preview_text.trim()) return toast.error("Inserisci il testo del messaggio");
    localStorage.setItem("vw_my_phone", phone);
    setPreviewing(true);
    try {
      const r = await api.campaignPreview({ phone, contest_id: form.contest_id || null, template_id: form.template_id ? Number(form.template_id) : null, preview_text: form.preview_text, filters });
      if (r.status === "failed") toast.error(`Anteprima non inviata: ${r.error || "errore Spoki"}`);
      else toast.success(r.mock ? "Anteprima simulata (vedi log WhatsApp del concorso)" : `Anteprima inviata a ${phone}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anteprima non riuscita");
    } finally {
      setPreviewing(false);
    }
  };

  const send = async () => {
    if (!form.preview_text.trim()) return toast.error("Inserisci il testo del messaggio");
    if (form.mode === "recurring") {
      if (!window.confirm(`Attivare la campagna ricorrente (${form.frequency === "monthly" ? "ogni mese" : "ogni settimana"} alle ${form.send_time}) solo per i nuovi contatti con consenso?`)) return;
      setBusy(true);
      try {
        await api.createRecurrence({ contest_id: form.contest_id || null, template_id: form.template_id ? Number(form.template_id) : null, preview_text: form.preview_text, name: form.name, filters, frequency: form.frequency, send_time: form.send_time, end_date: form.end_date || null });
        toast.success("Campagna ricorrente attivata");
        setForm((f) => ({ ...f, preview_text: "", name: "" }));
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Creazione non riuscita");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!rec?.messages) return toast.error("Nessun destinatario con consenso attivo");
    if (form.mode === "schedule" && !form.scheduled_at) return toast.error("Scegli data e ora di invio");
    const when = form.mode === "schedule" ? `il ${new Date(form.scheduled_at).toLocaleString("it-IT")}` : "adesso";
    if (!window.confirm(`Inviare la campagna ${when} a ${rec.messages} destinatari (${rec.unique_phones} numeri)?`)) return;
    setBusy(true);
    try {
      await api.createCampaign({
        contest_id: form.contest_id || null, template_id: form.template_id ? Number(form.template_id) : null,
        preview_text: form.preview_text, name: form.name,
        scheduled_at: form.mode === "schedule" ? form.scheduled_at : null,
        filters,
        discount,
      });
      toast.success(form.mode === "schedule" ? "Campagna programmata" : "Campagna avviata");
      setForm((f) => ({ ...f, preview_text: "", name: "", scheduled_at: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  const sample = contests.find((c) => c.id === form.contest_id)?.name || "Vivi Win";
  const preview = form.preview_text.replace(/\{NOME_CONCORSO\}/g, sample).replace(/\{PREMIO\}/g, "Buono spesa").replace(/\{SCONTO\}/g, `${form.discount_prefix || "SC"}-7K3M2P4Q`);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button data-testid="campaigns-back-button" variant="ghost" size="icon" onClick={() => navigate("/statistiche")}><ArrowLeft className="h-5 w-5" /></Button>
          <Logo className="h-10" />
          <div>
            <h1 className="font-display font-bold text-xl">Campagne WhatsApp</h1>
            <p className="text-xs text-muted-foreground">Messaggi promozionali ai contatti con consenso attivo</p>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8 grid lg:grid-cols-5 gap-6" data-testid="campaigns-page">
        <Card className="lg:col-span-2 p-5 bg-card border-border space-y-3 h-fit">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Megaphone className="h-4 w-4" /> Nuova campagna</h3>
          <div className="space-y-1.5">
            <Label className="text-xs">Concorso</Label>
            <select data-testid="campaign-contest-select" value={form.contest_id} onChange={(e) => setForm({ ...form, contest_id: e.target.value })} className={SEL}>
              <option value="">Tutti i concorsi</option>
              {contests.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Sede</Label>
              <select data-testid="campaign-store-select" value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} className={SEL}>
                <option value="">Tutte le sedi</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Premio vinto</Label>
              <select data-testid="campaign-prize-select" value={form.prize_id} onChange={(e) => setForm({ ...form, prize_id: e.target.value })} className={SEL} disabled={!form.contest_id}>
                <option value="">{form.contest_id ? "Tutti i premi" : "Scegli un concorso"}</option>
                {prizes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Stato ritiro</Label>
            <select data-testid="campaign-redeemed-select" value={form.redeemed} onChange={(e) => setForm({ ...form, redeemed: e.target.value })} className={SEL}>
              {REDEEM_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="rounded-lg bg-secondary/40 p-3 text-sm flex items-center gap-2" data-testid="campaign-recipients">
            <Users className="h-4 w-4 text-primary" />
            {rec ? <span><b>{rec.messages}</b> messaggi · <b>{rec.unique_phones}</b> numeri con consenso attivo</span> : "…"}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Nome campagna (facoltativo)</Label>
            <Input data-testid="campaign-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Promo settembre" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">ID template Spoki (facoltativo in simulazione)</Label>
            <Input data-testid="campaign-template" type="number" value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} placeholder="123" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Testo del messaggio · campi: {"{NOME_CONCORSO}"}, {"{PREMIO}"}{form.discount_on ? `, {SCONTO}` : ""}</Label>
            <textarea data-testid="campaign-text" value={form.preview_text} onChange={(e) => setForm({ ...form, preview_text: e.target.value })} rows={4} maxLength={1000}
              placeholder="Ciao! Grazie per aver partecipato a {NOME_CONCORSO}. Questa settimana ti aspetta una sorpresa in negozio…"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none" />
          </div>
          {preview && (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-3 text-sm whitespace-pre-wrap" data-testid="campaign-preview">
              <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1">Anteprima</div>
              {preview}
            </div>
          )}
          {form.mode !== "recurring" && (
            <div className="rounded-lg border border-border p-3 space-y-2" data-testid="campaign-discount-box">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="campaign-discount-toggle" checked={form.discount_on} onChange={(e) => setForm({ ...form, discount_on: e.target.checked })} className="h-4 w-4 accent-primary" />
                Allega codice sconto univoco per cliente (campo <span className="font-mono-code">{"{SCONTO}"}</span>)
              </label>
              {form.discount_on && (
                <div className="grid grid-cols-[1fr_80px_80px] gap-2">
                  <Input data-testid="campaign-discount-description" value={form.discount_description} onChange={(e) => setForm({ ...form, discount_description: e.target.value })} placeholder="Es. 10% sul prossimo acquisto" />
                  <Input data-testid="campaign-discount-days" type="number" min={1} max={365} value={form.discount_days} onChange={(e) => setForm({ ...form, discount_days: e.target.value })} title="Validità in giorni" />
                  <Input data-testid="campaign-discount-prefix" value={form.discount_prefix} onChange={(e) => setForm({ ...form, discount_prefix: e.target.value.toUpperCase() })} placeholder="SC" title="Prefisso" />
                </div>
              )}
              {form.discount_on && !form.preview_text.includes("{SCONTO}") && <p className="text-[11px] text-amber-400">Aggiungi {"{SCONTO}"} nel testo per includere il codice nel messaggio.</p>}
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Quando inviare</Label>
            <div className="grid grid-cols-3 gap-2">
              {[["now", "Invia subito"], ["schedule", "Programma"], ["recurring", "Ripeti"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`campaign-mode-${v}`} onClick={() => setForm({ ...form, mode: v })}
                  className={`rounded-md border px-3 py-2 text-sm ${form.mode === v ? "border-primary bg-primary/10" : "border-border"}`}>{l}</button>
              ))}
            </div>
            {form.mode === "schedule" && (
              <Input data-testid="campaign-schedule-input" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
            )}
            {form.mode === "recurring" && (
              <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3 space-y-2" data-testid="campaign-recurring-box">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Frequenza</Label>
                    <select data-testid="campaign-frequency" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className={SEL}>
                      <option value="weekly">Ogni settimana</option>
                      <option value="monthly">Ogni mese</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ora di invio</Label>
                    <Input data-testid="campaign-send-time" type="time" value={form.send_time} onChange={(e) => setForm({ ...form, send_time: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Data fine (facoltativa)</Label>
                  <Input data-testid="campaign-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
                <p className="text-[11px] text-muted-foreground">Ogni edizione viene inviata automaticamente solo ai nuovi contatti con consenso non ancora raggiunti da questa ricorrenza.</p>
              </div>
            )}
          </div>
          <div className="rounded-lg border border-border p-3 space-y-2" data-testid="campaign-self-test">
            <Label className="text-xs flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5" /> Il mio numero (per l'anteprima)</Label>
            <div className="flex gap-2">
              <Input data-testid="campaign-my-phone" value={myPhone} onChange={(e) => setMyPhone(e.target.value)} placeholder="+393331234567" />
              <Button data-testid="campaign-preview-send" variant="secondary" onClick={sendPreview} disabled={previewing} className="shrink-0 gap-1.5">
                <Send className="h-3.5 w-3.5" /> {previewing ? "Invio…" : "Invia anteprima al mio numero"}
              </Button>
            </div>
          </div>
          <Button data-testid="campaign-send" onClick={send} disabled={busy || (form.mode !== "recurring" && !rec?.messages)} className="w-full gap-2 h-11">
            <SubmitIcon mode={form.mode} />
            {submitLabel(form.mode, busy, rec)}
          </Button>
        </Card>

        <div className="lg:col-span-3 space-y-3">
          <CampaignCompare campaigns={campaigns} />
          {recurrences.length > 0 && (
            <>
              <h3 className="font-display font-bold flex items-center gap-2"><Repeat className="h-4 w-4 text-fuchsia-400" /> Campagne ricorrenti</h3>
              {recurrences.map((r) => <RecurrenceCard key={r.id} r={r} editions={campaigns.filter((c) => c.recurrence_id === r.id)} onChange={load} />)}
            </>
          )}
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold">Storico campagne</h3>
            <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
          {campaigns.length === 0 ? (
            <Card className="p-10 text-center border-dashed border-2 bg-card/50" data-testid="campaigns-empty"><p className="text-muted-foreground">Nessuna campagna inviata.</p></Card>
          ) : campaigns.map((c) => <CampaignRow key={c.id} c={c} onChange={load} />)}
        </div>
      </main>
    </div>
  );
}

function Countdown({ iso }) {
  const [left, setLeft] = useState(() => Math.max(0, new Date(iso) - Date.now()));
  useEffect(() => {
    const t = setInterval(() => setLeft(Math.max(0, new Date(iso) - Date.now())), 1000);
    return () => clearInterval(t);
  }, [iso]);
  const s = Math.floor(left / 1000);
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return <span className="font-mono-code">{d ? `${d}g ` : ""}{String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(sec).padStart(2, "0")}</span>;
}

function DiscountStats({ c }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.campaignDiscounts(c.id).then(setD).catch(() => {});
  }, [c.id]);
  if (!d) return null;
  const pct = d.total ? Math.round((d.redeemed / d.total) * 100) : 0;
  return (
    <div className="mt-2 flex items-center gap-2 text-xs rounded-md bg-amber-500/10 border border-amber-500/20 px-3 py-2" data-testid={`campaign-discount-stats-${c.id}`}>
      <span>🏷️ {c.discount.description}</span>
      <span className="ml-auto"><b>{d.redeemed}</b>/{d.total} riscattati ({pct}%) · {d.expired} scaduti</span>
      <a href={api.exportCampaignDiscountsUrl(c.id)} className="underline" data-testid={`campaign-discount-export-${c.id}`}>Excel</a>
    </div>
  );
}

function CampaignRow({ c, onChange }) {
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState(null);
  const [label, cls] = STATUS[c.status] || STATUS.running;
  const pct = progressPct(c);
  const filters = Object.values(c.filter_labels || {}).filter(Boolean);
  const act = async (fn, msg) => {
    try {
      await fn(c.id);
      toast.success(msg);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    }
  };
  const toggle = async () => {
    if (!open && replies === null) setReplies(await api.campaignReplies(c.id).catch(() => []));
    setOpen(!open);
  };
  return (
    <Card className="p-4 bg-card border-border" data-testid={`campaign-row-${c.id}`}>
      <div className="flex items-center gap-2">
        <h4 className="font-semibold flex-1 truncate">{c.name}</h4>
        {c.mock && <Badge className="border border-amber-500/30 bg-amber-500/15 text-amber-400">simulata</Badge>}
        <Badge className={`border ${cls}`} data-testid={`campaign-status-${c.id}`}>{label}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{c.recurrence_id && <span className="text-fuchsia-400">edizione {c.edition} · </span>}{c.contest_name}{filters.length ? ` · ${filters.join(" · ")}` : ""} · {fmtIt(c.created_at)} · {c.created_by}</p>
      {c.status === "scheduled" && (
        <div className="mt-2 flex items-center gap-2 rounded-md bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-xs" data-testid={`campaign-scheduled-${c.id}`}>
          <Clock className="h-3.5 w-3.5 text-amber-400" />
          <span>Invio il <b>{fmtIt(c.scheduled_at)}</b> · tra <Countdown iso={c.scheduled_at} /></span>
          <span className="ml-auto flex gap-1">
            <Button size="sm" variant="secondary" className="h-7 text-xs gap-1" onClick={() => act(api.campaignSendNow, "Campagna avviata")} data-testid={`campaign-sendnow-${c.id}`}><Send className="h-3 w-3" /> Invia ora</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-red-400" onClick={() => window.confirm("Annullare la campagna programmata?") && act(api.campaignCancel, "Campagna annullata")} data-testid={`campaign-cancel-${c.id}`}><XCircle className="h-3 w-3" /> Annulla</Button>
          </span>
        </div>
      )}
      <p className="text-xs mt-2 whitespace-pre-wrap line-clamp-2">{c.preview_text}</p>
      {c.status !== "cancelled" && (
        <>
          <div className="h-1.5 rounded-full bg-secondary mt-3 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
          <p className="text-xs mt-1" data-testid={`campaign-counts-${c.id}`}><span className="text-emerald-400">{c.sent} inviati</span> · <span className={c.failed ? "text-red-400" : "text-muted-foreground"}>{c.failed} falliti</span> · {c.total} totali ({c.unique_phones} numeri)</p>
        </>
      )}
      {c.status === "completed" && c.discount && <DiscountStats c={c} />}
      {c.status === "completed" && (
        <button type="button" onClick={toggle} className="mt-2 w-full flex items-center gap-2 text-xs rounded-md bg-secondary/40 px-3 py-2 hover:bg-secondary/60" data-testid={`campaign-replies-toggle-${c.id}`}>
          <MessageSquareReply className="h-3.5 w-3.5 text-primary" />
          <span><b>{c.replies || 0}</b> clienti hanno risposto · <b>{c.replays || 0}</b> hanno rigiocato</span>
          <span className="ml-auto">{open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</span>
        </button>
      )}
      {open && (
        <div className="mt-2 max-h-64 overflow-auto divide-y divide-border text-xs" data-testid={`campaign-replies-${c.id}`}>
          {!replies?.length ? <p className="py-3 text-muted-foreground text-center">Nessuna risposta ancora.</p> : replies.map((r) => (
            <div key={r.id} className="py-2 flex items-center gap-2">
              <span className="font-mono-code text-muted-foreground">{fmtIt(r.created_at)}</span>
              <span className="font-semibold">{r.phone}</span>
              <span className="font-mono-code truncate flex-1">{r.inbound_text}</span>
              <Badge className="border bg-secondary/60">{r.outcome}</Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
